import {useCallback, useEffect, useState} from "react";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import {useCacheRefresh} from "../../../hooks/useCacheRefresh";
import {CacheType} from "../../../constant/CodeSpecNum";
import {
    deleteDepartment,
    getDepartments,
    saveDepartment,
    updateDepartment,
} from "./DepartmentService";

function findInTree(nodes, id) {
    for (const node of nodes ?? []) {
        if (node.id === id) {
            return node;
        }
        const child = findInTree(node.children, id);
        if (child) {
            return child;
        }
    }
    return null;
}

export function useDepartmentManage() {
    const refreshCache = useCacheRefresh();
    const [listData, setListData] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadList = useCallback(async () => {
        const response = await getDepartments();
        if (response?.status === 200) {
            const content = response.data?.content ?? [];
            setListData(content);
            return content;
        }
        setListData([]);
        return [];
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        void loadList().finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [loadList]);

    useEffect(() => {
        if (!selectedDepartment?.id) {
            return;
        }
        setSelectedDepartment((prev) => {
            if (!prev) {
                return prev;
            }
            return findInTree(listData, prev.id) ?? null;
        });
    }, [listData, selectedDepartment?.id]);

    const refreshSelection = useCallback(async () => {
        const content = await loadList();
        setSelectedDepartment((prev) => {
            if (!prev?.id) {
                return prev;
            }
            return findInTree(content, prev.id);
        });
    }, [loadList]);

    const handleCreateRoot = async (name) => {
        const trimmed = name?.trim();
        if (!trimmed) {
            handleActiveToast({message: "부서명을 입력해주세요."});
            return;
        }

        const response = await saveDepartment({name: trimmed});
        if (response?.status === 200) {
            refreshCache(CacheType.department);
            handleActiveToast({message: "부서가 등록되었습니다."});
            await refreshSelection();
        } else if (response?.status === 202) {
            handleActiveError({message: response.data?.title, description: response.data?.reason});
        } else if (response?.data?.resultCode === "DEPARTMENT_NAME_DUPLICATED") {
            handleActiveToast({message: "등록할 수 없습니다.", description: "부서명이 중복되었습니다."});
        }
    };

    const handleCreateChild = async (parentId, name) => {
        const trimmed = name?.trim();
        if (!trimmed) {
            return;
        }

        const response = await saveDepartment({name: trimmed, parentId});
        if (response?.status === 200) {
            refreshCache(CacheType.department);
            handleActiveToast({message: "하위 부서가 등록되었습니다."});
            await refreshSelection();
        } else if (response?.status === 202) {
            handleActiveError({message: response.data?.title, description: response.data?.reason});
        } else if (response?.data?.resultCode === "DEPARTMENT_NAME_DUPLICATED") {
            handleActiveToast({message: "등록할 수 없습니다.", description: "부서명이 중복되었습니다."});
        }
    };

    const handleUpdate = async (department) => {
        if (!department?.id) {
            return;
        }

        const response = await updateDepartment(department);
        if (response?.status === 200) {
            refreshCache(CacheType.department);
            handleActiveToast({message: "수정되었습니다."});
            await refreshSelection();
        } else if (response?.data?.resultCode === "DEPARTMENT_NAME_DUPLICATED") {
            handleActiveToast({message: "수정할 수 없습니다.", description: "부서명이 중복되었습니다."});
        } else if (response?.data?.resultCode === "DEPARTMENT_NOT_FOUND") {
            handleActiveToast({message: "수정할 수 없습니다.", description: "부서가 존재하지 않습니다."});
        }
    };

    const handleUpdateChild = async (item) => {
        await handleUpdate({
            id: item.id,
            name: item.name,
            parentId: item.parentId,
            description: item.description,
            sortOrder: item.sortOrder,
        });
    };

    const handleDelete = async (id) => {
        const response = await deleteDepartment(id);
        if (response?.status === 200) {
            refreshCache(CacheType.department);
            handleActiveToast({message: "삭제되었습니다."});
            setSelectedDepartment((prev) => (prev?.id === id ? null : prev));
            await loadList();
        } else if (response?.data?.result === "DEPARTMENT_HAS_CHILD") {
            handleActiveToast({message: "삭제할 수 없습니다.", description: "하위 부서가 존재합니다."});
        } else if (response?.data?.resultCode === "DEPARTMENT_NOT_FOUND") {
            handleActiveToast({message: "삭제할 수 없습니다.", description: "부서가 존재하지 않습니다."});
        } else if (response?.status === 202) {
            handleActiveToast({message: "삭제할 수 없습니다.", description: "하위 부서가 존재합니다."});
        }
    };

    const setEditorDepartment = (updater) => {
        setSelectedDepartment((prev) => {
            const base = prev ? {...prev} : null;
            const next = typeof updater === "function" ? updater(base) : updater;
            if (!next) {
                return null;
            }
            const sortOrder = next.sortOrder ?? next.orders;
            return {...next, sortOrder, orders: undefined};
        });
    };

    const handleSaveEditor = async (item) => {
        await handleUpdate({
            id: item.id,
            name: item.name,
            description: item.description,
            sortOrder: item.orders ?? item.sortOrder,
            parentId: item.parentId,
        });
    };

    return {
        listData,
        selectedDepartment,
        setSelectedDepartment,
        setEditorDepartment,
        loading,
        handleCreateRoot,
        handleCreateChild,
        handleSaveEditor,
        handleUpdateChild,
        handleDelete,
    };
}
